// Local person pipeline (sender side, SA B1/B3/B8):
//   camera ─▶ downscale ─▶ Selfie Segmenter ─▶ soft alpha mask
//                       ├▶ Pose Landmarker (lite, every frame) ─▶ 33 keypoints (x, y, z, vis)
//                       └▶ Face Landmarker (every 2nd frame)  ─▶ iris centres → eyes / IPD / roll
//
// The outgoing WebRTC video is a "packed" frame:
//   ┌──────────────┬──────────────┐
//   │  RGB (w×h)   │  mask (w×h)  │   video codecs carry no alpha, so the mask
//   ├──────────────┴──────────────┤   travels as a grey image next to the colour
//   │ seq barcode strip (BAR_H)   │   image; the strip carries the frame's seq
//   └─────────────────────────────┘
// and, for every frame, a PoseFrame {seq, t, keypoints, face, colorStats} goes
// over the data channel. The receiver reads the seq from the decoded video and
// joins the pose of exactly that frame (render/stage.ts).
//
// Everything is un-mirrored camera space; nothing is flipped on the sender.
//
// In "greenscreen" mode (real green backdrop, or a weak phone) the mask comes
// from chroma keying instead of the neural net; the wire format is the same.
// Models are bundled under public/models (no CDN at runtime, CSP 'self').

import {
  FaceDetector, FaceLandmarker, FilesetResolver, ImageSegmenter, PoseLandmarker,
} from '@mediapipe/tasks-vision';
import { PROC_WIDTH, STREAM_FPS, assetUrl } from '../config';
import { chromaKeyPixels } from '../booth/chroma';
import { labStats } from '../booth/color';
import { AnchorFilter, DEFAULT_FACE, faceFromAnchor, smoothFace, type FaceBox } from '../booth/layout';
import { decontaminate, guidedUpsample, lumaOf } from '../booth/matte';
import { anchorOf, inFaceSkin } from '../booth/parts';
import { LM_MIN, beautyParams, loadBeauty, type BeautySettings } from '../booth/beauty';
import { BeautyGL } from './beauty-gl';
import {
  BAR_CELLS, BAR_H, KP_COUNT, PoseRing, barcodeCells, encodePose, faceFromEyes, quantizePose,
  type ColorStats, type FaceInfo, type PoseFrame, type PoseFrameMsg,
} from '../booth/pose';
import { perfCounters, registry } from './registry';

import type { KeyMode } from '../net/protocol';
export type { KeyMode };

interface Vision {
  seg: ImageSegmenter;
  pose: PoseLandmarker | null;
  face: FaceLandmarker | null;
  /** legacy fallback when the landmarker models are not bundled */
  blaze: FaceDetector | null;
}

let visionPromise: Promise<Vision> | null = null;

const optional = async <T>(what: string, p: Promise<T>): Promise<T | null> => {
  try { return await p; } catch (e) { console.warn(`[pipeline] ${what} unavailable`, e); return null; }
};

async function createTasks(delegate: 'GPU' | 'CPU'): Promise<Vision> {
  const fileset = await FilesetResolver.forVisionTasks(assetUrl('mediapipe/wasm'));
  const seg = await ImageSegmenter.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: assetUrl('models/selfie_segmenter.tflite'), delegate },
    runningMode: 'VIDEO',
    outputConfidenceMasks: true,
    outputCategoryMask: false,
  });
  const pose = await optional('PoseLandmarker', PoseLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: assetUrl('models/pose_landmarker_lite.task'), delegate },
    runningMode: 'VIDEO',
    numPoses: 1,
    minPoseDetectionConfidence: 0.5,
    minPosePresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
    outputSegmentationMasks: false,
  }));
  const face = await optional('FaceLandmarker', FaceLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: assetUrl('models/face_landmarker.task'), delegate },
    runningMode: 'VIDEO',
    numFaces: 1,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: false,
  }));
  const blaze = face ? null : await optional('FaceDetector', FaceDetector.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: assetUrl('models/blaze_face_short_range.tflite'), delegate },
    runningMode: 'VIDEO',
    minDetectionConfidence: 0.5,
  }));
  return { seg, pose, face, blaze };
}

export function loadVision() {
  visionPromise ??= createTasks('GPU').catch((e) => {
    console.warn('GPU delegate failed, falling back to CPU', e);
    return createTasks('CPU');
  });
  return visionPromise;
}

/** Face Landmarker iris centres (478-point model): 468 and 473. */
const IRIS_A = 468, IRIS_B = 473;

/** What the booth needs from a camera pipeline (worker-based or the legacy main-thread one). */
export interface CameraPipeline {
  readonly packed: HTMLCanvasElement;
  readonly stream: MediaStream;
  readonly poses: PoseRing;
  pose: PoseFrame | null;
  mode: KeyMode;
  face: FaceBox;
  faceSeen: boolean;
  fps: number;
  mlMs: number;
  frameMs: number;
  level: number;
  framesTotal: number;
  beauty: BeautySettings;
  sticker: string;
  mirrored: boolean;
  readonly noFace: boolean;
  /** seq of the frame in `packed` when known (null → read the barcode) */
  readonly packedSeq: number | null;
  /** changes with every new packed frame */
  readonly frameId: number;
  onFace: ((f: FaceBox) => void) | null;
  onPose: ((p: PoseFrame) => void) | null;
  addPoseListener(cb: (p: PoseFrame) => void): () => void;
  sourceChanged(mirrored: boolean): void;
  start(): Promise<void>;
  stop(): void;
  holdMl(untilMs: number): void;
  grabFrame(): GrabbedFrame;
  finishCutout(f: GrabbedFrame, masked?: boolean, pick?: number): Promise<Cutout>;
}

/** Legacy (v0.6) pipeline: everything on the main thread. Kept as the fallback. */
export class PersonPipeline implements CameraPipeline {
  /** v0.7 face stickers need the worker pipeline */
  sticker = 'none';
  readonly packedSeq = null;
  get frameId() { return this.framesTotal; }
  readonly packed: HTMLCanvasElement;
  readonly stream: MediaStream;
  mode: KeyMode = 'ai';
  /** legacy face box (derived from the eye anchor), for v2 peers and old consumers */
  face: FaceBox = DEFAULT_FACE;
  faceSeen = false;
  fps = 0;
  onFace: ((f: FaceBox) => void) | null = null;
  /** v0.5: every processed frame's pose (also pushed into `poses`) */
  onPose: ((p: PoseFrame) => void) | null = null;
  /** recent frames' poses keyed by seq (the local stage layer joins them by barcode like a remote one) */
  readonly poses = new PoseRing();
  /** latest pose frame */
  pose: PoseFrame | null = null;
  /** smoothed ML time per frame (ms) */
  mlMs = 0;
  /** v0.6 "Làm đẹp": personal retouch, applied before the frame leaves the phone (live + still) */
  beauty: BeautySettings = loadBeauty();
  /**
   * Automatic quality step-down (0 = full, 1 = lighter, 2 = power saving):
   * fewer landmark passes and bilateral taps when a frame costs too much of the
   * 33 ms budget, back up after a calm stretch.
   */
  level = 0;
  /** smoothed cost of a whole frame (ML + retouch + packing), ms */
  frameMs = 0;

  private proc = document.createElement('canvas');
  private procCtx: CanvasRenderingContext2D;
  private packedCtx: CanvasRenderingContext2D;
  private maskData: ImageData;
  private maskPrev: Float32Array;
  private maskLow: Float32Array;
  private tiny = document.createElement('canvas');
  private tinyCtx: CanvasRenderingContext2D;
  private running = false;
  private lastTs = 0;
  /** ML is skipped until this time (keeps the main thread free right before a shot). */
  private holdUntil = 0;
  private frameCount = 0;
  /** frames processed since start (device tests) */
  framesTotal = 0;
  private fpsT0 = performance.now();
  private vision: Vision | null = null;
  private seq = 0;
  private kp: Float32Array | null = null;
  private eyes: FaceInfo | null = null;
  private color: ColorStats | null = null;
  private anchorF = new AnchorFilter();
  /** Face Landmarker mesh of the latest face, normalised (x, y) pairs */
  private faceLm: Float32Array | null = null;
  private bgl: BeautyGL | null | undefined;
  private slowS = 0;
  private calmS = 0;
  private listeners = new Set<(p: PoseFrame) => void>();

  /** front camera → selfie; false after switching to the rear camera (informational: the stage mirrors as a whole) */
  mirrored = true;
  private lastFaceAt = 0;
  private loopGen = 0;

  constructor(private readonly video: HTMLVideoElement) {
    this.procCtx = this.proc.getContext('2d', { willReadFrequently: true })!;
    this.tinyCtx = this.tiny.getContext('2d', { willReadFrequently: true })!;
    this.packed = document.createElement('canvas');
    this.packedCtx = this.packed.getContext('2d')!;
    this.maskData = new ImageData(2, 2);
    this.maskPrev = new Float32Array(4);
    this.maskLow = new Float32Array(4);
    this.resize();
    this.stream = this.packed.captureStream(STREAM_FPS);
    const track = this.stream.getVideoTracks()[0];
    if (track && 'contentHint' in track) track.contentHint = 'detail';
    registry.pipelines.add(this);
  }

  addPoseListener(cb: (p: PoseFrame) => void) {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }

  /** (Re)derive processing sizes from the camera, e.g. after switching front/rear. */
  resize() {
    const vw = this.video.videoWidth || 640;
    const vh = this.video.videoHeight || 480;
    const w = Math.min(PROC_WIDTH, vw) & ~1;
    const h = Math.round((w * vh) / vw) & ~1;
    if (w === this.proc.width && h === this.proc.height) return;
    this.proc.width = w;
    this.proc.height = h;
    this.packed.width = w * 2;
    this.packed.height = h + BAR_H;
    this.maskData = new ImageData(w, h);
    this.maskPrev = new Float32Array(w * h);
    this.maskLow = new Float32Array(w * h);
    this.tiny.width = 64;
    this.tiny.height = Math.max(8, Math.round((64 * h) / w));
    this.packedCtx.fillStyle = '#000';
    this.packedCtx.fillRect(0, 0, w * 2, h + BAR_H);
  }

  /** Camera changed (front ↔ rear). */
  sourceChanged(mirrored: boolean) {
    this.mirrored = mirrored;
    this.faceSeen = false;
    this.lastFaceAt = 0;
    this.face = DEFAULT_FACE;
    this.kp = null;
    this.eyes = null;
    this.faceLm = null;
    this.anchorF.reset();
    this.resize();
    if (this.running) this.loop();
  }

  /** True when no face has been detected for a while (e.g. rear camera on the scenery). */
  get noFace() {
    return performance.now() - this.lastFaceAt > 1500;
  }

  get procSize() {
    return { w: this.proc.width, h: this.proc.height };
  }

  /**
   * Starts the frame loop. Rejects when the models cannot be loaded — the loop
   * keeps running anyway (chroma key / last mask), so the outgoing video never
   * freezes; the caller typically switches to 'greenscreen'.
   */
  async start() {
    this.running = true;
    this.loop();
    this.vision = await loadVision();
  }

  private loop() {
    const gen = ++this.loopGen; // a new loop retires the previous one (camera switch)
    const step = () => {
      if (!this.running || gen !== this.loopGen) return;
      this.tick();
      const v = this.video as any;
      if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(step);
      else requestAnimationFrame(step);
    };
    step();
  }

  stop() {
    this.running = false;
    this.stream.getTracks().forEach((t) => t.stop());
    registry.pipelines.delete(this);
    this.listeners.clear();
  }

  /** MediaPipe VIDEO mode needs strictly increasing timestamps across all calls. */
  private nextTs() {
    const t = Math.max(performance.now(), this.lastTs + 0.01);
    this.lastTs = t;
    return t;
  }

  /** Skip ML until `untilMs` (frames keep flowing with the last mask and pose). */
  holdMl(untilMs: number) {
    this.holdUntil = Math.max(this.holdUntil, untilMs);
  }

  private tick() {
    const { w, h } = this.procSize;
    const now = performance.now();
    const ml = now >= this.holdUntil;
    this.procCtx.drawImage(this.video, 0, 0, w, h);
    const t0 = performance.now();
    const seq = this.seq = (this.seq + 1) & 0xffff;

    if (ml) {
      if (this.mode === 'ai' && this.vision && (this.level < 2 || seq % 2 === 0)) {
        const res = this.vision.seg.segmentForVideo(this.proc, this.nextTs());
        const mask = res.confidenceMasks?.[0];
        if (mask) this.writeMask(mask.getAsFloat32Array(), this.maskData.data, this.maskPrev, this.maskLow);
        res.close();
      } else if (this.mode === 'greenscreen') {
        // real green backdrop: key on the sender so the wire format stays identical
        // (works without any model — also the fallback when the models fail to load)
        const img = this.procCtx.getImageData(0, 0, w, h);
        const px = img.data;
        chromaKeyPixels(px);
        const out = this.maskData.data;
        for (let j = 0, i = 0; j < px.length; j += 4, i++) {
          out[j] = out[j + 1] = out[j + 2] = px[j + 3];
          out[j + 3] = 255;
          this.maskLow[i] = px[j + 3] / 255;
          px[j + 3] = 255;
        }
        this.procCtx.putImageData(img, 0, 0); // spill-suppressed colour
      }
      if (this.vision) this.landmarks(seq);
      if (seq % 6 === 0) this.measureColor();
    }
    // Always emit a frame: colour + the latest mask (while ML is held the mask is
    // simply reused — the outgoing video never freezes, in either mode).
    const bp = beautyParams(this.beauty, this.faceLm, w, h);
    const g = bp && this.beautyGl();
    if (bp && g) {
      // colour | mask both go through the retouch warp (one GPU pass)
      this.packedCtx.drawImage(g.renderPacked(this.proc, this.maskData, w, h, bp), 0, 0);
    } else {
      this.packedCtx.drawImage(this.proc, 0, 0);
      this.packedCtx.putImageData(this.maskData, w, 0);
    }
    this.writeBarcode(seq, w, h);
    const ft = performance.now() - t0;
    this.frameMs = this.frameMs ? this.frameMs * 0.9 + ft * 0.1 : ft;
    if (ml) {
      const dt = performance.now() - t0;
      this.mlMs = this.mlMs ? this.mlMs * 0.9 + dt * 0.1 : dt;
      perfCounters.mlMs = this.mlMs;
    }

    const frame = quantizePose({ seq, t: now, aspect: w / h, kp: this.kp, face: this.eyes, color: this.color });
    this.pose = frame;
    this.poses.push(frame);
    this.onPose?.(frame);
    this.listeners.forEach((fn) => fn(frame));

    this.frameCount++;
    this.framesTotal++;
    if (now - this.fpsT0 > 1000) {
      this.fps = (this.frameCount * 1000) / (now - this.fpsT0);
      this.frameCount = 0;
      this.fpsT0 = now;
      this.govern();
    }
  }

  /** Once a second: step quality down after 3 s over budget, up after 10 calm seconds. */
  private govern() {
    const budget = 1000 / STREAM_FPS;
    if (this.frameMs > budget * 0.8) { this.slowS++; this.calmS = 0; } else if (this.frameMs < budget * 0.4) { this.calmS++; this.slowS = 0; } else { this.slowS = 0; this.calmS = 0; }
    const before = this.level;
    if (this.slowS >= 3 && this.level < 2) this.level++;
    else if (this.calmS >= 10 && this.level > 0) this.level--;
    if (this.level !== before) {
      this.slowS = this.calmS = 0;
      console.info(`[pipeline] quality level ${before} → ${this.level} (frame ${this.frameMs.toFixed(1)} ms)`);
    }
    if (this.bgl) this.bgl.taps = [16, 10, 6][this.level];
  }

  /** Lazily created; null when the phone has no WebGL2 (then no retouch, everything else works). */
  private beautyGl() {
    if (this.bgl === undefined) this.bgl = BeautyGL.create();
    return this.bgl;
  }

  private writeBarcode(seq: number, w: number, h: number) {
    const ctx = this.packedCtx;
    const W = w * 2, cw = Math.floor(W / BAR_CELLS);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, h, W, BAR_H);
    ctx.fillStyle = '#fff';
    barcodeCells(seq).forEach((on, i) => { if (on) ctx.fillRect(i * cw, h, cw, BAR_H); });
  }

  /**
   * Confidence → grey mask. A soft ramp (the receiver's guided filter sharpens
   * the edge along the real image edge), temporal EMA only where the mask is
   * stable so moving arms do not leave ghost trails.
   */
  private writeMask(m: Float32Array, out: Uint8ClampedArray, prev: Float32Array | null, low: Float32Array | null) {
    for (let i = 0, j = 0; i < m.length; i++, j += 4) {
      let v = m[i];
      if (prev) {
        if (Math.abs(prev[i] - v) < 0.3) v = prev[i] * 0.3 + v * 0.7;
        prev[i] = v;
      }
      const a = Math.min(1, Math.max(0, (v - 0.12) / 0.76));
      if (low) low[i] = a;
      const b = a * 255;
      out[j] = out[j + 1] = out[j + 2] = b;
      out[j + 3] = 255;
    }
  }

  private landmarks(seq: number) {
    const v = this.vision!;
    const { w, h } = this.procSize;
    if (v.pose && (this.level < 2 || seq % 2 === 1)) {
      const r = v.pose.detectForVideo(this.proc, this.nextTs());
      const lm = r.landmarks?.[0];
      if (lm && lm.length >= KP_COUNT) {
        const kp = this.kp ?? new Float32Array(KP_COUNT * 4);
        for (let i = 0; i < KP_COUNT; i++) {
          kp[i * 4] = lm[i].x; kp[i * 4 + 1] = lm[i].y; kp[i * 4 + 2] = lm[i].z; kp[i * 4 + 3] = lm[i].visibility ?? 0;
        }
        this.kp = kp;
      } else this.kp = null;
      (r as unknown as { close?: () => void }).close?.();
    }
    if (v.face && seq % (this.level + 2) === 0) {
      const r = v.face.detectForVideo(this.proc, this.nextTs());
      const lm = r.faceLandmarks?.[0];
      if (lm && lm.length > IRIS_B) {
        this.eyes = faceFromEyes([lm[IRIS_A].x, lm[IRIS_A].y], [lm[IRIS_B].x, lm[IRIS_B].y], w / h);
        if (lm.length >= LM_MIN) {
          const out = this.faceLm ?? new Float32Array(LM_MIN * 2);
          for (let i = 0; i < LM_MIN; i++) { out[i * 2] = lm[i].x; out[i * 2 + 1] = lm[i].y; }
          this.faceLm = out;
        }
        this.gotFace();
      } else if (performance.now() - this.lastFaceAt > 300) { this.eyes = null; this.faceLm = null; }
    } else if (v.blaze && seq % 3 === 0) {
      const r = v.blaze.detectForVideo(this.proc, this.nextTs());
      const best = r.detections.filter((d) => d.boundingBox)
        .sort((a, b) => b.boundingBox!.width * b.boundingBox!.height - a.boundingBox!.width * a.boundingBox!.height)[0];
      // BlazeFace keypoints 0/1 are the eyes
      const k = best?.keypoints;
      if (k && k.length >= 2) {
        this.eyes = faceFromEyes([k[0].x, k[0].y], [k[1].x, k[1].y], w / h);
        this.gotFace();
      }
    }
  }

  private gotFace() {
    this.lastFaceAt = performance.now();
    const a = this.anchorF.filter(anchorOf({ seq: 0, t: 0, aspect: 1, kp: null, face: this.eyes, color: null }, this.face), this.lastFaceAt);
    this.face = smoothFace(this.faceSeen ? this.face : null, faceFromAnchor(a), 0.6);
    this.faceSeen = true;
    this.onFace?.(this.face);
  }

  /** Lab mean/std of the face skin (person region when no face) on a 64-px thumbnail (SA B6.3). */
  private measureColor() {
    const { w, h } = this.procSize;
    const tw = this.tiny.width, th = this.tiny.height;
    this.tinyCtx.drawImage(this.proc, 0, 0, tw, th);
    const px = this.tinyCtx.getImageData(0, 0, tw, th).data;
    const wt = new Float32Array(tw * th);
    const face = this.eyes ? anchorOf({ seq: 0, t: 0, aspect: w / h, kp: null, face: this.eyes, color: null }, this.face) : null;
    for (let y = 0; y < th; y++) for (let x = 0; x < tw; x++) {
      const m = this.maskLow[Math.min(h - 1, Math.floor(((y + 0.5) * h) / th)) * w + Math.min(w - 1, Math.floor(((x + 0.5) * w) / tw))];
      wt[y * tw + x] = face && !inFaceSkin(face, tw, th, x + 0.5, y + 0.5) ? 0 : m;
    }
    const s = labStats(px, wt);
    if (s) this.color = s;
  }

  /**
   * Step 1 of a shot, called at the synchronised instant: grabs the current
   * camera frame at full resolution plus the pose of the latest frame. Cheap
   * (one drawImage), so its timing is not disturbed by the ML work, which
   * happens afterwards in finishCutout().
   */
  grabFrame(): GrabbedFrame {
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    const c = document.createElement('canvas');
    c.width = vw;
    c.height = vh;
    c.getContext('2d', { willReadFrequently: true })!.drawImage(this.video, 0, 0, vw, vh);
    return {
      canvas: c, at: performance.now(), face: { ...this.face }, mirrored: this.mirrored, noFace: this.noFace,
      pose: this.pose ? { ...this.pose, aspect: vw / vh } : null,
      faceLm: this.faceLm ? this.faceLm.slice() : null, beauty: { ...this.beauty },
    };
  }

  /**
   * Step 2: segment the grabbed frame.
   *  - `full`: full-resolution transparent WebP → uploaded to the server (paid renders)
   *  - `preview`: ≤ PREVIEW_WIDTH px → shared with everyone in the room instantly
   * The mask is guided-filter upsampled to the camera resolution and the edge band
   * is decontaminated (F·α), so the still has no halo (SA B3).
   * With `masked = false` (the member whose real room is the backdrop) the frame is kept whole.
   */
  async finishCutout(f: GrabbedFrame, masked = true): Promise<Cutout> {
    const c = f.canvas;
    if (!c) throw new Error('khung ảnh không hợp lệ');
    const vw = c.width, vh = c.height;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    // the same retouch as the live view, at full resolution (same normalised landmarks)
    const bp = f.beauty ? beautyParams(f.beauty, f.faceLm ?? null, vw, vh) : null;
    const g = bp && this.beautyGl();
    if (bp && g) ctx.drawImage(g.renderStill(c, vw, vh, bp), 0, 0);
    if (masked) {
      const img = ctx.getImageData(0, 0, vw, vh);
      let alpha: Float32Array | null = null;
      if (this.mode === 'ai' && this.vision) {
        const { w, h } = this.procSize;
        const small = document.createElement('canvas');
        small.width = w;
        small.height = h;
        small.getContext('2d')!.drawImage(c, 0, 0, w, h);
        const res = this.vision.seg.segmentForVideo(small, this.nextTs());
        const m = res.confidenceMasks?.[0]?.getAsFloat32Array();
        res.close();
        if (m) {
          const low = new Float32Array(w * h);
          this.writeMask(m, new Uint8ClampedArray(w * h * 4), null, low);
          alpha = guidedUpsample(low, w, h, lumaOf(img.data, vw, vh), vw, vh);
        }
      } else {
        chromaKeyPixels(img.data);
        alpha = new Float32Array(vw * vh);
        for (let i = 0; i < alpha.length; i++) alpha[i] = img.data[i * 4 + 3] / 255;
      }
      if (alpha) decontaminate(img.data, alpha, vw, vh);
      ctx.putImageData(img, 0, 0);
    }
    const pw = Math.min(PREVIEW_WIDTH, vw);
    const ph = Math.round((pw * vh) / vw);
    const pc = document.createElement('canvas');
    pc.width = pw;
    pc.height = ph;
    const pctx = pc.getContext('2d')!;
    pctx.imageSmoothingQuality = 'high';
    pctx.drawImage(c, 0, 0, pw, ph);
    const toBlob = (cv: HTMLCanvasElement, q: number) => new Promise<Blob>((res, rej) =>
      cv.toBlob((b) => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/webp', q));
    const [full, preview] = await Promise.all([toBlob(c, 0.92), toBlob(pc, 0.8)]);
    return {
      full, preview, previewBitmap: await createImageBitmap(pc), face: f.face, masked, mirrored: f.mirrored, noFace: f.noFace,
      pose: f.pose ? encodePose(f.pose) : null,
    };
  }
}

/** Preview cut-outs shared peer-to-peer are deliberately small. */
export const PREVIEW_WIDTH = 480;

export interface Cutout {
  full: Blob;
  /** v0.8: `full` is the original colour | this phone's matte, side by side — the server re-mattes it */
  packed?: boolean;
  preview: Blob;
  previewBitmap: ImageBitmap;
  face: FaceBox;
  masked: boolean;
  mirrored: boolean;
  noFace: boolean;
  /** v0.5: pose of the captured frame (wire format) */
  pose: PoseFrameMsg | null;
}

export interface GrabbedFrame {
  /** legacy pipeline: the full-resolution frame */
  canvas?: HTMLCanvasElement;
  /** worker pipeline: id of the frame kept in the worker */
  id?: number;
  /** worker pipeline: frames around the instant (local times) — the phones agree on one */
  candidates?: Promise<{ k: number; t: number; motion: number }[]>;
  at: number;
  face: FaceBox;
  mirrored: boolean;
  noFace: boolean;
  pose: PoseFrame | null;
  /** v0.6: face mesh + retouch settings of the shutter instant */
  faceLm?: Float32Array | null;
  beauty?: BeautySettings;
}

/** Worker pipeline when the browser supports it (v0.7), else the main-thread one. */
export async function createPipeline(video: HTMLVideoElement): Promise<CameraPipeline> {
  const { WorkerPipeline, workerPipelineSupported } = await import('./worker-pipeline');
  if (workerPipelineSupported()) {
    try { return new WorkerPipeline(video); } catch (e) { console.warn('[pipeline] worker pipeline unavailable', e); }
  }
  return new PersonPipeline(video);
}
